import { isEmpty, noop } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { ImageWithFallback, LoaderAnim, PreviewPhoto } from '@sm360/widget-commons-basic-components';
import { LocaleService, ContentZonesService } from '@sm360/widget-commons-core-services';
import { TileDisclaimer } from '@sm360/widget-commons-tiles';
import { PurchaseMethods, CurrencyFormats, Vehicle, ModelYear } from '@sm360/widget-commons-vehicle';

import config from '../../../config/index.js';

import Specification from './Specification/Specification.jsx';

import './InventoryNewTile.scss';

const { PurchaseMethodsValues: PURCHASE_METHODS } = PurchaseMethods;
const { PaymentDetail, TileTag, URL } = Vehicle;

function getFinanceTypeForDisclaimers(currentMethod, TileFinanceType) {
    switch (currentMethod) {
        case PURCHASE_METHODS.FINANCE:
            return TileFinanceType.FINANCE;
        case PURCHASE_METHODS.LEASE:
            return TileFinanceType.LEASE;
        case PURCHASE_METHODS.CASH:
            return TileFinanceType.CASH;
        default:
            return '';
    }
}

// https://jira.sm360.ca/browse/STS-636
// Desactivation temporaire - voir billet
/*
const getVehicleAmountAndPurchaseMethodName = (term, termName) => ({
    amount: LocaleService.l(term.getFinancedAmount(), CurrencyFormats.CURRENCY_0),
    method: LocaleService.t(`VEHICLE_TILE.${termName.toUpperCase()}_METHOD_NAME`),
});

const getBestPriceBlock = (vehicle) => {
    const currentMethod = vehicle.purchaseMethods.getSelectedPurchaseMethodValue();
    if (currentMethod !== PURCHASE_METHODS.CASH) {
        return null;
    }

    const cashPurchaseAmounts = vehicle.purchaseMethods.getCashPurchaseAmounts();
    const cashDiscount = Math.round(cashPurchaseAmounts.rebates);

    let amountAndName = null;

    const lease = vehicle.purchaseMethods.lease.getSelectedTerm();
    const leaseHigherThanCash = lease && lease.getDiscountsAndRebates() > cashDiscount;
    if (leaseHigherThanCash) {
        amountAndName = getVehicleAmountAndPurchaseMethodName(lease, 'lease');
    }

    const finance = vehicle.purchaseMethods.finance.getSelectedTerm();
    const financeHigherThanCash = finance && finance.getDiscountsAndRebates() > cashDiscount;
    const financeHigherThanLease = !lease || lease.getDiscountsAndRebates() < finance.getDiscountsAndRebates();
    if (financeHigherThanCash && financeHigherThanLease) {
        amountAndName = getVehicleAmountAndPurchaseMethodName(finance, 'financing');
    }

    if (!amountAndName) {
        return null;
    }

    return (
        <div className="has-secondary-background is-price sr-disclaimer is-bold pay-less-financing">
            <icon className="icon-promo2" />
            <span>{LocaleService.t('VEHICLE_TILE.PAY_LESS_FINANCING', amountAndName)}</span>
        </div>
    );
};
*/

const getCZDisclaimer = () => {
    return ContentZonesService.getContentZone('widget.listing.used', 'disclaimer.new')
        ? ContentZonesService.getContentZone('widget.listing.used', 'disclaimer.new').getValue()
        : '';
};

const InventoryNewTile = ({ onClick, vehicle, isLoading }) => {
    const cashPurchaseAmounts = vehicle.purchaseMethods.getCashPurchaseAmounts();
    const totalRebates = vehicle.purchaseMethods.highRebate + cashPurchaseAmounts.dealerDiscounts;

    const taxCodes = vehicle.purchaseMethods.getTaxCodesString()
        ? vehicle.purchaseMethods.getTaxCodesString()
        : LocaleService.t('taxes.taxes');

    const save = totalRebates > 0 && (
        <div className="listing-tile-car-save">
            <icon className="icon-promo2 is-price" />
            <span>
                {LocaleService.t('MAX_DISCOUNT', {
                    price: LocaleService.l(totalRebates, CurrencyFormats.CURRENCY_0),
                })}
            </span>
        </div>
    );

    const vehicleElectricRebate = vehicle.purchaseMethods.getElectricRebate();
    const vehicleElectricRebateAmount = vehicleElectricRebate
        ? Number.parseInt(vehicleElectricRebate.rebate, 10)
        : null;
    const showElectricRebateIncludedIcon =
        !Number.isNaN(vehicleElectricRebateAmount) && vehicleElectricRebateAmount > 0;
    const electricRebate = showElectricRebateIncludedIcon && (
        <div className="listing-tile-car-save electricRebate">
            <icon className="icon-electric-vehicle" />
            <span>{LocaleService.t('VEHICLE_STATUS.ELECTRIC_REBATE')}</span>
        </div>
    );

    const vehicleBoniRebate = vehicle.purchaseMethods.getBonusRebate();
    const boniRebate = vehicleBoniRebate > 0 && (
        <div className="listing-tile-car-boni">
            <icon className="icon-plus" /> {LocaleService.t('VEHICLE_STATUS.BONI')}
            <span>{LocaleService.l(vehicleBoniRebate, CurrencyFormats.CURRENCY_0)}</span>
        </div>
    );

    const driveTrain = !isEmpty(vehicle.driveTrain) && <p>{vehicle.driveTrain}</p>;
    const transmission = !isEmpty(vehicle.transmission) && <p>{vehicle.transmission}</p>;
    const engine = !isEmpty(vehicle.engine) && <p>{vehicle.engine}</p>;
    const currentMethod = vehicle.purchaseMethods.getSelectedPurchaseMethodValue();

    const stockNumber = !!vehicle.stockNumber && (
        <p className="sr-disclaimer listing-tile-specification-stock">
            {LocaleService.t('SUMMARY.STOCK_NUMBER', { stock: vehicle.stockNumber })}
        </p>
    );

    const hasConfigurationToken = !!(vehicle && vehicle.configurationToken);
    return (
        <div id={vehicle.vehicleId} className="listing-new-tile" onClick={hasConfigurationToken ? onClick : ''}>
            <div className="listing-new-tile-image">
                <div className="listing-new-rebate">
                    {save}
                    {electricRebate}
                    {boniRebate}
                </div>
                <div className="new-car-image-wrapper">
                    {!isLoading ? (
                        <ImageWithFallback
                            alt={vehicle.getYearMakeModel()}
                            className="car-image"
                            imageUrl={vehicle.multimedia.getCatalogSideImage360x197()}
                            fallbackImageUrl={`${config.source.image}/ir/w360h197c/images/${URL.NO_PHOTO}`}
                        />
                    ) : (
                        <LoaderAnim />
                    )}
                    <TileTag
                        shouldRender={vehicle.locked}
                        icon="icon-lock"
                        background="#F5273F"
                        color="#fff"
                        text={LocaleService.t('VEHICLE_STATUS.RESERVED')}
                    />
                    {vehicle.exteriorColourStatus < ModelYear.EXTERIOR_COLOUR_STATUSES.FULL_COLORIZED &&
                        vehicle.exteriorColourStatus > 0 && (
                            <p className="vehicle-image-status is-light">
                                {LocaleService.t('MISSING_PHOTO.REFERENCE_PHOTO')}
                            </p>
                        )}
                </div>
            </div>
            <div className="listing-new-tile-wrapper">
                <div className="listing-new-tile-drivePowerTrains">
                    <div className="new-car-name sr-text is-bold">
                        {vehicle.getYearModel()} <span className="sr-disclaimer">{vehicle.modelTrim}</span>
                    </div>
                    <div className="new-car-motor">
                        {driveTrain}
                        {transmission}
                        {engine}
                    </div>
                </div>

                <div className="listing-tiles-payment">
                    <PaymentDetail purchaseMethods={vehicle.purchaseMethods} taxCodes={taxCodes} />
                </div>

                {/* https://jira.sm360.ca/browse/STS-636 */}
                {/* Desactivation temporaire - voir billet */}
                {/* {getBestPriceBlock(vehicle)} */}

                <TileDisclaimer
                    vehiclePurchaseMethod={vehicle.purchaseMethods}
                    currentFinanceType={getFinanceTypeForDisclaimers(currentMethod, TileDisclaimer.FINANCE_TYPE)}
                    czDisclaimer={getCZDisclaimer()}
                />
                <div className="listing-new-tile-spec">
                    <Specification vehicle={vehicle} />
                    <div className="listing-tile-specification-specifics">
                        {stockNumber}
                        <PreviewPhoto
                            imageUrl={vehicle.multimedia.getMainImage360x270()}
                            noPhotoUrl={URL.NO_PHOTO}
                            pictures={vehicle.multimedia.pictures}
                            yearMakeModel={vehicle.getYearMakeModel()}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

InventoryNewTile.defaultProps = {
    onClick: noop,
};

InventoryNewTile.propTypes = {
    onClick: PropTypes.func,
    vehicle: PropTypes.instanceOf(Vehicle.VehicleModel).isRequired,
    isLoading: PropTypes.bool.isRequired,
};

InventoryNewTile.displayName = 'Tile-A.InventoryNewTile';

export default InventoryNewTile;



// WEBPACK FOOTER //
// /builds/development/frontend/widgets/widgets/components/tiles/tile-a/src/components/Tile/InventoryNew/InventoryNewTile.jsx